'use client'

import type { Space } from '@cocoonspace/sdk-js/types/space'
import {
	type CSSProperties,
	Fragment,
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react'
import { Zone } from '@cocoonspace/sdk-js/types/zone'
import { Card } from '@cocoonspace/ui/components/card'
import { useIsMobile } from '@cocoonspace/ui/hooks/use-mobile'
import { LocateIcon, MapPinIcon } from 'lucide-react'
import { type MapRef, Marker } from 'react-map-gl/maplibre'

import { MapLibre, Popup } from '~/components/maplibre'
import { SpacesCarousel } from '~/domains/search/components/search-map/spaces-popup'
import { useSpaceAddress } from '../../hooks/use-space-address.hook'
import { useSpacesSuggestions } from '../../hooks/use-space-details-page.hook'

type MarkerClickEvent = {
	originalEvent: {
		stopPropagation: VoidFunction
	}
}

export const MarkerCocoon = ({ space }: { space: Space }) => {
	const { fullAddress } = useSpaceAddress(space, true)

	return (
		<Marker
			latitude={space.latitude}
			longitude={space.longitude}
		>
			<a
				href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
					fullAddress,
				)}`}
				rel='noreferrer'
				target='_blank'
			>
				<MapPinIcon className='fill-primary !size-10 text-transparent [&>circle]:fill-white' />
			</a>
		</Marker>
	)
}

const SuggestionsMarkers = forwardRef<
	{ closePopup: VoidFunction },
	{
		onMarkerClick: (pos: { latitude: number; longitude: number }) => void
		spaces: Space[]
		currentVenueName: string
	}
>(({ spaces, currentVenueName, onMarkerClick }, ref) => {
	const [selectedVenueName, setSelectedVenueName] = useState<string | null>(
		null,
	)

	useImperativeHandle(ref, () => ({
		closePopup: () => setSelectedVenueName(null),
	}))

	const grouppedSpacesPerVenue = useMemo(() => {
		return spaces
			.filter((space) => space.venue_name !== currentVenueName)
			.reduce<Record<string, Space[]>>((acc, curr) => {
				if (acc[curr.venue_name]) {
					acc[curr.venue_name].push(curr)
				} else {
					acc[curr.venue_name] = [curr]
				}
				return acc
			}, {})
	}, [spaces, currentVenueName])

	const handleMarkerClick = useCallback(
		(e: MarkerClickEvent, spaces: Space[]) => {
			e.originalEvent.stopPropagation()
			setSelectedVenueName(spaces[0].venue_name)
			onMarkerClick({ ...spaces[0] })
		},
		[onMarkerClick],
	)

	return Object.entries(grouppedSpacesPerVenue).map(([venueName, spaces]) => (
		<Fragment key={venueName}>
			<Marker
				latitude={spaces[0].latitude}
				longitude={spaces[0].longitude}
				onClick={(e) => handleMarkerClick(e, spaces)}
			>
				<div className='bg-primary size-8 rounded-full border-white outline outline-4 outline-offset-[-5px] outline-white' />
			</Marker>

			{selectedVenueName === venueName && (
				<Popup
					anchor='bottom'
					closeOnClick={false}
					latitude={spaces[0].latitude}
					longitude={spaces[0].longitude}
					onClose={() => setSelectedVenueName(null)}
				>
					<Card>
						<SpacesCarousel spaces={spaces} />
					</Card>
				</Popup>
			)}
		</Fragment>
	))
})

const CenterButton = ({ onClick }: { onClick: VoidFunction }) => {
	return (
		<button
			className='absolute right-10 bottom-6 z-10 rounded-full bg-white p-2 shadow hover:bg-slate-100 max-lg:hidden xl:right-20'
			onClick={onClick}
			type='button'
		>
			<LocateIcon className='!size-8' />
		</button>
	)
}

const defaultMapStyle = { height: 500 }
const ZOOM_LEVEL = 15

export const SpaceMap = ({
	showSuggestions = false,
	space,
	style,
}: {
	showSuggestions?: boolean
	space: Space
	style?: CSSProperties
}) => {
	const isMobile = useIsMobile()

	const initialViewState = useMemo(
		() => ({
			latitude: space.latitude,
			longitude: space.longitude,
			zoom: ZOOM_LEVEL,
		}),
		[space],
	)

	const _style = useMemo(
		() => ({
			...defaultMapStyle,
			...style,
		}),
		[style],
	)

	const mapRef = useRef<MapRef>(null)

	const { data: suggestions } = useSpacesSuggestions({
		capacity: space?.capacity ?? 0,
		zone: space.zone as Zone,
		position: {
			latitude: space.latitude ?? 0,
			longitude: space.longitude ?? 0,
		},
		enabled: showSuggestions,
	})

	const moveMapToSpace = useCallback(() => {
		mapRef.current?.flyTo({
			center: [space.longitude, space.latitude],
			zoom: ZOOM_LEVEL,
		})
	}, [space.longitude, space.latitude])

	const handleOnClick = useCallback(() => {
		suggestionsRef.current?.closePopup()
	}, [])

	useEffect(() => {
		if (isMobile) {
			moveMapToSpace()
		}
	}, [isMobile, moveMapToSpace])

	const suggestionsRef = useRef<{ closePopup: VoidFunction }>(null)

	const handleMarkerClick = useCallback(
		(pos: { latitude: number; longitude: number }) => {
			mapRef.current?.flyTo({
				center: [pos.longitude, pos.latitude + 0.0008],
				zoom: ZOOM_LEVEL,
			})
		},
		[],
	)

	if (!space) return null

	return (
		<MapLibre
			cursor='default'
			doubleClickZoom={!isMobile}
			dragPan={!isMobile}
			initialViewState={initialViewState}
			onClick={handleOnClick}
			ref={mapRef}
			scrollZoom={false}
			style={_style}
			touchPitch={false}
			touchZoomRotate={false}
		>
			<MarkerCocoon space={space} />

			{showSuggestions && (
				<SuggestionsMarkers
					currentVenueName={space.venue_name}
					onMarkerClick={handleMarkerClick}
					ref={suggestionsRef}
					spaces={suggestions ?? []}
				/>
			)}

			<CenterButton onClick={moveMapToSpace} />
		</MapLibre>
	)
}
